import { EventBus } from "../core/event-bus"
import { AUDIO, EVENT } from "../core/keys"
import { THEME } from "../core/theme"
import type { GameData } from "../types"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors

interface SfxRuntimeOptions {
  /** Used to read scene.time.now for rate-limiting. */
  getNow: () => number
  vfx: VfxManager
  getGameData: () => GameData
  playSfx: (key: string, volume?: number) => void
}

export class SfxRuntime {
  private readonly options: SfxRuntimeOptions

  private lastBallBounceSfxAt = Number.NEGATIVE_INFINITY
  private lastComboTierSfxAt = Number.NEGATIVE_INFINITY
  private lastBallStateSfxAt = Number.NEGATIVE_INFINITY
  private suppressBumperHitUntil = Number.NEGATIVE_INFINITY

  constructor(options: SfxRuntimeOptions) {
    this.options = options
  }

  public register(): void {
    EventBus.on(EVENT.PLAYER_LEVELUP, this.handleLevelUpSfx, this)
    EventBus.on(EVENT.UPGRADE_CHOSEN, this.handleUpgradeChosenSfx, this)
    EventBus.on(EVENT.SHOP_OPENED, this.handleShopOpenedSfx, this)
    EventBus.on(EVENT.BALL_LAUNCHED, this.handleBallLaunchSfx, this)
    EventBus.on(EVENT.BALL_BOUNCE, this.handleBallBounceSfx, this)
    EventBus.on(EVENT.BALL_EXPIRED, this.handleBallExpireSfx, this)
    EventBus.on(EVENT.BALL_STATE_CHANGED, this.handleBallStateSfx, this)
    EventBus.on(EVENT.COMBO_TIER_REACHED, this.handleComboTierSfx, this)
  }

  public unregister(): void {
    EventBus.off(EVENT.PLAYER_LEVELUP, this.handleLevelUpSfx, this)
    EventBus.off(EVENT.UPGRADE_CHOSEN, this.handleUpgradeChosenSfx, this)
    EventBus.off(EVENT.SHOP_OPENED, this.handleShopOpenedSfx, this)
    EventBus.off(EVENT.BALL_LAUNCHED, this.handleBallLaunchSfx, this)
    EventBus.off(EVENT.BALL_BOUNCE, this.handleBallBounceSfx, this)
    EventBus.off(EVENT.BALL_EXPIRED, this.handleBallExpireSfx, this)
    EventBus.off(EVENT.BALL_STATE_CHANGED, this.handleBallStateSfx, this)
    EventBus.off(EVENT.COMBO_TIER_REACHED, this.handleComboTierSfx, this)
  }

  // ── Bumper & launcher shots ───────────────────────────────────

  public playBumperHit(): void {
    const now = this.options.getNow()
    if (now < this.suppressBumperHitUntil) return

    const player = this.options.getGameData().player
    const nextHits = Math.min(player.combo.targetHits, player.combo.hits + 1)
    const comboProgress = nextHits / Math.max(1, player.combo.targetHits)
    this.options.playSfx(AUDIO.BUMPER, 1 + comboProgress)
    this.options.playSfx(AUDIO.SFX_RICOCHET, 1 + comboProgress)
  }

  public suppressBumperHits(durationMs: number): void {
    this.suppressBumperHitUntil = Math.max(
      this.suppressBumperHitUntil,
      this.options.getNow() + durationMs,
    )
  }

  public playLauncherShot(): void {
    this.options.playSfx(AUDIO.BARK, 0.95)
  }

  // ── EventBus handlers ─────────────────────────────────────────

  private handleLevelUpSfx(): void {
    this.options.playSfx(AUDIO.SFX_LEVEL_UP, 1.0)
  }

  private handleUpgradeChosenSfx(): void {
    this.options.playSfx(AUDIO.SFX_UPGRADE_PICK, 0.9)
  }

  private handleShopOpenedSfx(): void {
    this.options.playSfx(AUDIO.SFX_SHOP_OPEN, 0.85)
  }

  private handleBallLaunchSfx(): void {
    this.options.playSfx(AUDIO.BALL_LAUNCH, 0.75)
  }

  private handleBallBounceSfx(payload?: { kickback?: boolean }): void {
    const now = this.options.getNow()
    const minGap = payload?.kickback ? 30 : 55
    if (now - this.lastBallBounceSfxAt < minGap) return
    this.lastBallBounceSfxAt = now
    this.options.playSfx(AUDIO.BALL_BOUNCE, payload?.kickback ? 1.0 : 0.72)
  }

  private lastBallExpireSfxAt = Number.NEGATIVE_INFINITY

  private handleBallExpireSfx(): void {
    const now = this.options.getNow()
    if (now - this.lastBallExpireSfxAt < 50) return
    this.lastBallExpireSfxAt = now
    this.options.playSfx(AUDIO.BALL_EXPIRE, 0.82)
  }

  private handleBallStateSfx(): void {
    const now = this.options.getNow()
    if (now - this.lastBallStateSfxAt < 60) return
    this.lastBallStateSfxAt = now
    this.options.playSfx(AUDIO.SFX_BALL_STATE, 0.95)
  }

  private handleComboTierSfx(payload?: { tier?: number }): void {
    const tier = payload?.tier || 1
    const now = this.options.getNow()
    if (now - this.lastComboTierSfxAt < 80) return
    this.lastComboTierSfxAt = now

    const tierBoost =
      tier === 4 ? 1.5 : tier === 3 ? 1.2 : tier === 2 ? 1.0 : 0.8
    this.options.playSfx(AUDIO.SFX_COMBO_TIER, tierBoost)

    const colors = [
      COLOR.white,
      COLOR.info,
      COLOR.warning,
      COLOR.danger,
      COLOR.cyanBright,
    ]
    const color = colors[Math.min(tier, 4)]
    this.options.vfx.flashScreen(color, 0.15 + tier * 0.05, 150 + tier * 50)
    this.options.vfx.shakeCamera(100 + tier * 50, 0.004 * tier)
  }
}
